import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ConfigGeneral.scss';
import Tabs from 'devextreme-react/tabs';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { useAppSelector } from '../../contexts/hooks';
import { Config_General } from '../../types/types.config';
import { GroupsConfigurations } from '../../utils/systemTypes';
import { Form, Label, Item as ControlItem, GroupItem } from 'devextreme-react/form';
import { Button, ScrollView, Tooltip, LoadIndicator } from 'devextreme-react';
import { newToast } from '../../components';
import { RequiredRule, CustomRule } from 'devextreme-react/data-grid';
import { useApiConfiguration } from '../../api/api.configuration';
import { formatMessage } from 'devextreme/localization';
import { useScreenSize } from '../../utils/media-query';

const Index = () => {
  const Prm_Conf = useAppSelector((x) => x.userInfo.Permissions?.Prm_Conf);
  const Configuracion: Config_General[] = useAppSelector((x) => x.configuration.Data?.Config_General ?? []);
  const [currentGroup, setCurrentGroup] = useState<number>(1);
  const [EditItem, setEditItem] = useState<boolean>(true);
  const [data, setData] = useState<any>({});
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { setConfig } = useApiConfiguration();
  const frmRef = useRef<Form>(null);
  const { isMedium, isLarge } = useScreenSize();

  useEffect(() => {
    let formData = {};
    Configuracion.forEach((item) => {
      formData[item.Cgn_Nombre] =
        item.Cgn_Control === 'dxSwitch'
          ? item.Cgn_Resultado === 'true' // Convertir string a booleano
          : item.Cgn_Resultado;
    });
    frmRef.current?.instance.updateData(formData);
    setData(formData); // Guarda el estado inicial para enviar los datos correctamente
  }, [Configuracion]);

  const handleClicGroup = useCallback((e) => {
    setCurrentGroup(e.itemData.Value);
  }, []);

  const onTabsSelectionChanged = useCallback(
    async (args) => {
      const currentForm = frmRef.current?.instance;

      if (currentForm) {
        const validationResult = currentForm.validate();

        // Asegurar que hay reglas rotas
        if (validationResult?.brokenRules?.length && EditItem) {
          // Filtrar campos inválidos
          const invalidFields = validationResult.brokenRules.filter((rule) => {
            const fieldName = rule.type; // Obtener el nombre del campo directamente
            return (
              rule.type === 'required' &&
              fieldName && // Verificar que el campo existe
              (data[fieldName] === undefined || data[fieldName] === null || data[fieldName] === '')
            );
          });

          // Mostrar mensaje solo si hay campos realmente inválidos
          if (invalidFields.length > 0) {
            newToast('WARNING', 'Hay campos obligatorios pendientes por llenar', 5);
            return;
          }
        }
      }

      if (typeof args.value === 'number') {
        setSelectedTab(args.value);
      }
    },
    [frmRef, EditItem, data]
  );

  const handleEditClick = useCallback(() => {
    if (!Prm_Conf) {
      newToast('WARNING', 'No cuenta con permisos para editar la configuración', 5);
      setEditItem(true);
      return;
    }
    setEditItem(false);
  }, []);

  const handleSaveClick = useCallback(async () => {
    if (frmRef.current) {
      if (frmRef.current?.instance.validate().isValid) {
        setIsSaving(true);
        try {
          await setConfig(data);
          setEditItem(true);
        } catch (error) {
          console.error('Error al guardar configuración:', error);
        } finally {
          setIsSaving(false);
        }
      } else {
        newToast('WARNING', 'Ingrese todos los datos requeridos', 5);
      }
    }
  }, [data, setConfig]);

  // Función para renderizar un control individual
  const renderControlItem = (item: Config_General) => {
    const isPasswordField = item.Cgn_EsContrasena === true;

    switch (item.Cgn_Control) {
      case 'dxSwitch':
        return (
          <ControlItem
            key={item.Cgn_Id}
            editorType={item.Cgn_Control}
            editorOptions={{
              value: data[item.Cgn_Nombre] === true || data[item.Cgn_Nombre] === 'true',
              onValueChanged: (e) => {
                const newValue = e.value === 'true' ? true : e.value === 'false' ? false : e.value;
                setData((prev) => {
                  const updatedData = { ...prev, [item.Cgn_Nombre]: newValue };
                  return updatedData;
                });
              },
            }}
            disabled={EditItem}
            dataField={item.Cgn_Nombre}
            helpText={item.Cgn_Descripcion ? item.Cgn_Descripcion : ''}>
            <Label text={item.Cgn_Label?.toUpperCase()} />
            <CustomRule validationCallback={(e) => typeof e.value === 'boolean'} message='El campo es obligatorio' />
          </ControlItem>
        );
      case 'dxSelectBox':
        return (
          <ControlItem
            key={item.Cgn_Id}
            editorType={item.Cgn_Control}
            editorOptions={{
              items: JSON.parse(item.Cng_ControlListas),
              displayExpr: 'label',
              valueExpr: 'value',
            }}
            disabled={EditItem}
            dataField={item.Cgn_Nombre}
            helpText={item.Cgn_Descripcion ? item.Cgn_Descripcion : ''}>
            <Label text={item.Cgn_Label?.toUpperCase()} />
            {item.Cng_Requerido === true && <RequiredRule />}
          </ControlItem>
        );
      case 'dxTagBox':
        return (
          <ControlItem
            key={item.Cgn_Id}
            dataField={item.Cgn_Nombre}
            editorType='dxTextBox'
            disabled={EditItem}
            helpText={item.Cgn_Descripcion ? item.Cgn_Descripcion : ''}>
            <Label text={item.Cgn_Label?.toUpperCase()} />
            {item.Cng_Requerido === true && <RequiredRule />}
          </ControlItem>
        );
      default:
        return (
          <ControlItem key={item.Cgn_Id} editorType={isPasswordField ? 'dxTextBox' : item.Cgn_Control} editorOptions={isPasswordField ? { mode: 'password' } : undefined} disabled={EditItem} dataField={item.Cgn_Nombre} helpText={item.Cgn_Descripcion ? item.Cgn_Descripcion : ''}>
            <Label text={item.Cgn_Label?.toUpperCase()} />
            {item.Cng_Requerido === true && <RequiredRule />}
          </ControlItem>
        );
    }
  };

  // Función para renderizar los items del formulario agrupados
  const renderFormItems = () => {
    // Filtrar configuraciones del grupo actual
    const currentGroupConfig = Configuracion.filter((item) => item.Cng_Grupo === currentGroup);

    // Si no hay configuraciones para el grupo actual, devolver array vacío para mostrar formulario vacío
    if (currentGroupConfig.length === 0) {
      return [];
    }

    // Agrupar por Cgn_SubGrupo
    const groupedConfig = currentGroupConfig.reduce((groups, item) => {
      const subGroup = item.Cgn_SubGrupo || 'Sin Grupo';
      if (!groups[subGroup]) {
        groups[subGroup] = [];
      }
      groups[subGroup].push(item);
      return groups;
    }, {} as Record<string, Config_General[]>);

    // Si solo hay un grupo y es "Sin Grupo", renderizar sin agrupación
    const groupKeys = Object.keys(groupedConfig);
    if (groupKeys.length === 1 && groupKeys[0] === 'Sin Grupo') {
      return groupedConfig['Sin Grupo'].map(renderControlItem);
    }

    // Renderizar con agrupación - subgrupos en una columna, campos en 2 columnas
    return Object.entries(groupedConfig).map(([subGroupName, items]) => (
      <GroupItem key={subGroupName} caption={subGroupName !== 'Sin Grupo' ? subGroupName : undefined} colCount={2} colCountByScreen={{ sm: 1, md: 2 }}>
        {items.map(renderControlItem)}
      </GroupItem>
    ));
  };

  return (
    <div className='view-wrapper view-wrapper-task-list'>
      <ScrollView className='view-wrapper-scroll' height={'100%'} width={'100%'}>
        <Toolbar className='toolbar-common' multiline={true}>
          <Item location='before' visible={isLarge || isMedium}>
            <span className='toolbar-header'>Configuración general</span>
          </Item>
          <Item location='before' widget='dxTabs' cssClass='tabs-container'>
            <Tabs defaultSelectedIndex={0} dataSource={GroupsConfigurations} width={isLarge || isMedium ? 'auto' : 300} onItemClick={handleClicGroup} onOptionChanged={onTabsSelectionChanged} scrollByContent={true} showNavButtons={true} scrollingEnabled={true} />
          </Item>
          <Item location='after' locateInMenu='auto' widget='dxButton' showText='inMenu'>
            <Button id='toolBarEdit' text={formatMessage('toolBarEdit')} stylingMode='outlined' icon='edit' type='default' onClick={handleEditClick} visible={EditItem && !isSaving} />
          </Item>
          <Item location='after' locateInMenu='auto' widget='dxButton' showText='inMenu'>
            <Button id='toolBarSave' text={formatMessage('toolBarSave')} stylingMode='outlined' icon='save' type='default' onClick={handleSaveClick} visible={!EditItem && !isSaving} />
          </Item>
          <Item location='after' locateInMenu='never' visible={isSaving}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LoadIndicator height={20} width={20} />
              <span>Guardando...</span>
            </div>
          </Item>
        </Toolbar>
        <div className='formStyle'>
          <form>
            <Form formData={data} ref={frmRef} labelLocation='top' minColWidth={233} colCount={1} screenByWidth={screenByWidth}>
              {renderFormItems()}
            </Form>
          </form>
        </div>
      </ScrollView>
      <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#toolBarSave'}>
        {formatMessage('toolBarSave')}
      </Tooltip>
      <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#toolBarEdit'}>
        {formatMessage('toolBarEdit')}
      </Tooltip>
    </div>
  );
};

function screenByWidth(width) {
  return width < 720 ? 'sm' : 'md';
}

export default Index;
